import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  CustomerInquiryListItemResponse,
  BaseButtonSize,
  BaseInquiryStatus,
  baseInquiryActions,
  BaseCoreState,
  baseFormsActions,
  BASE_PERMISSIONS,
  BaseRolesService,
  InquiryResponse,
  BaseModalService,
} from 'base';
import { BaseCustomerInquiryService } from '../../../inquiry.service';

@Component({
  selector: 'base-customer-inquiry-list-item',
  templateUrl: './base-customer-inquiry-list-item.component.html',
})
export class BaseCustomerInquiryListItemComponent implements OnInit {
  @ViewChild('staffRequest') staffRequest: ElementRef;
  @ViewChild('closeInquiryModal') closeInquiryModal: ElementRef;

  @Input() inquiry: CustomerInquiryListItemResponse;
  @Input() canPublishInquiry = false;
  @Input() copiedInquiry: InquiryResponse;
  @Input() filters: { [key in any]: any };

  baseInquiryStatus = BaseInquiryStatus;
  buttonSizes = BaseButtonSize;
  canPreselectOffers = false;
  newStaffRequestModalRef: NgbModalRef;
  onInquiryCloseModalRef: NgbModalRef;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<BaseCoreState>,
    private baseCustomerInquiryService: BaseCustomerInquiryService,
    private rolesService: BaseRolesService,
    private modalService: BaseModalService
  ) {}

  ngOnInit() {
    this.canPreselectOffers = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PRESELECT_OFFERS);
  }

  handleInquiryClick(inquiry: CustomerInquiryListItemResponse) {
    if (inquiry.status === BaseInquiryStatus.unpublished) {
      this.onEdit(inquiry.id as number);

      return;
    }

    const unlimited = inquiry.unlimited;

    if (unlimited) {
      this.router.navigate([`inquiries/${inquiry.id}/unlimited/offers`]);
    } else {
      this.router.navigate([`inquiries/${inquiry.id}/offers`]);
    }
  }

  copyInquiryHandler(event: Event, inquiry: CustomerInquiryListItemResponse) {
    event.stopPropagation();
    this.copyInquiry(inquiry.id as number);
  }

  copyInquiry(inquiryId: number) {
    this.store.dispatch(baseFormsActions.clearAccountRegisterForm());
    this.baseCustomerInquiryService.getInquiry(inquiryId).subscribe((inquiry) => {
      this.copiedInquiry = { ...inquiry, id: undefined };

      const isMixedShifts = this.baseCustomerInquiryService.defineIsShiftTypesMixed(this.copiedInquiry);

      if (isMixedShifts) {
        this.baseCustomerInquiryService.openShiftViewPage(this.copiedInquiry);
      } else {
        this.openNewStaffRequestModal();
      }
    });
  }

  handleClick(actionType: string, id: number) {
    switch (actionType) {
      case 'action.edit':
        this.onEdit(id);
        break;
      case 'action.close':
        this.onClose(id);
        break;
      case 'action.publish':
        this.onPublish(id);
        break;
    }
  }

  openNewStaffRequestModal() {
    this.newStaffRequestModalRef = this.modalService.open(
      {
        headText: 'inquiry.new',
        bodyTemplate: this.staffRequest,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );
    this.newStaffRequestModalRef.result.then(noop, noop);
  }

  private onEdit(inquiryId: number) {
    this.baseCustomerInquiryService.getInquiry(inquiryId).subscribe((inquiry) => {
      this.baseCustomerInquiryService.openShiftViewPage(inquiry, inquiryId);
    });
  }

  private onClose(inquiryId: number): void {
    this.onInquiryCloseModalRef = this.modalService.open(
      {
        headText: 'inquiry.closeInquiry.headText',
        bodyTemplate: this.closeInquiryModal,
        hideFooter: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'lg',
      }
    );

    this.onInquiryCloseModalRef.result.then((value: string) => {
      this.updateCustomerInquiryStatus(inquiryId, BaseInquiryStatus.closed, value);
    }, noop);
  }

  private onPublish(inquiryId: number): void {
    const modalRef = this.modalService.open(
      {
        headText: 'inquiry.publishInquiryNow',
        bodyText: 'inquiry.publishInquiryConfirmText',
        okText: 'action.yesPublish',
        cancelButtonBackground: 'secondary',
        cancelButtonDisplayed: true,
      },
      {
        scrollable: false,
        backdrop: true,
        size: 'md',
      }
    );

    modalRef.result.then(() => {
      this.updateCustomerInquiryStatus(inquiryId, BaseInquiryStatus.published);
    }, noop);
  }

  private updateCustomerInquiryStatus(inquiry: number, status: 1 | 2 | 3, closedNote?: string): void {
    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: {
          inquiry,
          status,
          closedNote,
          callback: () => {
            this.store.dispatch(
              baseInquiryActions.getInquiries({
                payload: this.activatedRoute.snapshot.queryParams,
              })
            );
          },
        },
      })
    );
  }
}
